import { defineStore } from 'pinia';
import type { SketchLineObject } from '@puppycad/cad-core';
import { EntityInteractionEventType, isSketchLineObject } from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObject,
} from './viewer-selection-helpers';

/**
 * 线参数状态（统一绘制中与选中时的参数来源）。
 */
export interface LineParametersSnapshot {
  /** 是否为构造线。 */
  isConstruction: boolean;
  /** 是否为无限长度。 */
  isInfiniteLength: boolean;
  /** 长度。 */
  length: number | null;
  /** 角度（度）。 */
  angleDeg: number | null;
}

/**
 * 创建空线参数快照。
 * @returns 空快照对象。
 */
function createEmptyLineParametersSnapshot(): LineParametersSnapshot {
  return {
    isConstruction: false,
    isInfiniteLength: false,
    length: null,
    angleDeg: null,
  };
}

/**
 * 线选择状态 Store。
 * 职责：
 * 1. 维护当前选中线对象；
 * 2. 统一派发选中线变化流；
 * 3. 同步画线预览参数与选中线参数。
 */
export const useGetSelectLine = defineStore('get-select-line', () => {
  let selectedLine: SketchLineObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSelectionSubscribed = false;
  let hasDrawingSubscribed = false;
  const lineChanged$ = new BehaviorSubject<SketchLineObject | null>(null);
  /** 统一的线参数流（绘制预览参数 或 选中线参数）。 */
  const lineParameters$ = new BehaviorSubject<LineParametersSnapshot>(createEmptyLineParametersSnapshot());
  const { getViewer } = useViewer();
  /**
   * 从线对象读取参数快照。
   * @param line 线对象，null 时返回空参数。
   * @returns 线参数快照。
   */
  function snapshotFromLine(line: SketchLineObject | null): LineParametersSnapshot {
    if (!line) {
      return createEmptyLineParametersSnapshot();
    }
    const length = line.getLength();
    const angleDeg = line.angleDeg;
    return {
      isConstruction: line.isConstruction,
      isInfiniteLength: line.isInfiniteLength,
      length: Number.isFinite(length) ? length : null,
      angleDeg: Number.isFinite(angleDeg) ? angleDeg : null,
    };
  }

  /**
   * 从实体选择管理器同步当前选中线。
   */
  function syncFromManager(): void {
    selectedLine = resolveViewerSelectedObject(getViewer(), isSketchLineObject);
    lineChanged$.next(selectedLine);
    lineParameters$.next(snapshotFromLine(selectedLine));
  }

  /**
   * 处理线选中事件。
   */
  function handleLineSelected(): void {
    syncFromManager();
  }

  /**
   * 处理选择清空事件。
   */
  function handleSelectionCleared(): void {
    selectedLine = null;
    lineChanged$.next(null);
    lineParameters$.next(createEmptyLineParametersSnapshot());
  }

  function subscribe(): void {
    const viewer = getViewer();
    const lineSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.LineSelected);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    const lineDrawingParameters$ = viewer?.observeLineDrawingParameters?.();
    if (!lineSelected$ || !selectionCleared$ || !lineDrawingParameters$) {
      return;
    }
    if (hasSelectionSubscribed === false) {
      syncFromManager();
      subscriptions.push(
        lineSelected$.subscribe(handleLineSelected),
        selectionCleared$.subscribe(handleSelectionCleared),
      );
      hasSelectionSubscribed = true;
    }
    if (hasDrawingSubscribed === false) {
      subscriptions.push(
        lineDrawingParameters$.subscribe((previewLine: SketchLineObject | null) => {
          if (previewLine) {
            lineParameters$.next(snapshotFromLine(previewLine));
            return;
          }
          lineParameters$.next(snapshotFromLine(selectedLine));
        }),
      );
      hasDrawingSubscribed = true;
    }
  }

  /**
   * 取消线选择与画线参数事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSelectionSubscribed = false;
    hasDrawingSubscribed = false;
  }

  /**
   * 显式同步当前选中线与参数。
   */
  function syncCurrentSelection(): void {
    syncFromManager();
  }

  return {
    getSelectedLine: (): SketchLineObject | null => selectedLine,
    onLineChanged: (): Observable<SketchLineObject | null> => lineChanged$.asObservable(),
    onLineParametersChanged: (): Observable<LineParametersSnapshot> => lineParameters$.asObservable(),
    subscribe,
    unsubscribe,
    syncCurrentSelection,
  };
});
